// YouTube IFrame Player API behind the PlayerAdapter contract (F4: the player is a guest — its
// own controls stay on, nothing is drawn over it, speed = whatever availableRates() returns).
//
// Time source: ~10 Hz getCurrentTime() polling. The IFrame API has no timeupdate event, and
// polling with setInterval keeps running in a hidden tab (throttled to ≥1 Hz, which is still
// enough for loop/auto-pause), where rAF is suspended outright. Native seeks (the iframe's own
// scrubber, arrow keys) are detected as discontinuities in the poll and reported via onSeek.
// Embedded YouTube pausing itself when the tab is hidden is NOT fought — it surfaces as a
// plain 'paused' state change.

import type { RateReportingAdapter } from './engine';

// ------------------------------------------------ minimal YT typing (no @types/youtube)

export interface YTPlayer {
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  getPlaybackRate(): number;
  setPlaybackRate(rate: number): void;
  getAvailablePlaybackRates(): number[];
  destroy(): void;
  /** Optional module API (captions track discovery in the Add wizard, best-effort). */
  loadModule?(name: string): void;
  getOption?(module: string, option: string): unknown;
}

export interface YTPlayerEvent<T = undefined> {
  target: YTPlayer;
  data: T;
}

export interface YTPlayerOptions {
  videoId?: string;
  width?: string | number;
  height?: string | number;
  playerVars?: Record<string, string | number>;
  events?: {
    onReady?: (e: YTPlayerEvent) => void;
    onStateChange?: (e: YTPlayerEvent<number>) => void;
    onPlaybackRateChange?: (e: YTPlayerEvent<number>) => void;
    onError?: (e: YTPlayerEvent<number>) => void;
  };
}

export interface YTNamespace {
  Player: new (el: HTMLElement | string, opts: YTPlayerOptions) => YTPlayer;
  PlayerState: {
    UNSTARTED: -1;
    ENDED: 0;
    PLAYING: 1;
    PAUSED: 2;
    BUFFERING: 3;
    CUED: 5;
  };
  /** Present once the script has executed; runs cb when the API is fully initialised. */
  ready?: (cb: () => void) => void;
}

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';
const PS = { UNSTARTED: -1, ENDED: 0, PLAYING: 1, PAUSED: 2, BUFFERING: 3, CUED: 5 } as const;

// ------------------------------------------------ script loader

let apiPromise: Promise<YTNamespace> | null = null;

/**
 * Injects https://www.youtube.com/iframe_api once and resolves with the YT namespace.
 * Idempotent: repeated calls share one promise; resolves immediately if window.YT.Player
 * already exists (e.g. the script was loaded by other means). Chains a pre-existing
 * window.onYouTubeIframeAPIReady instead of clobbering it.
 */
export function loadYouTubeIframeApi(): Promise<YTNamespace> {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise<YTNamespace>((resolve, reject) => {
    const yt = window.YT;
    if (yt?.Player) {
      resolve(yt);
      return;
    }
    if (yt?.ready) {
      yt.ready(() => resolve(window.YT as YTNamespace));
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve(window.YT as YTNamespace);
    };
    if (!document.querySelector(`script[src="${IFRAME_API_SRC}"]`)) {
      const s = document.createElement('script');
      s.src = IFRAME_API_SRC;
      s.async = true;
      s.onerror = () => {
        apiPromise = null; // allow a retry
        reject(new Error('YouTube IFrame API failed to load'));
      };
      document.head.appendChild(s);
    }
  });
  return apiPromise;
}

// ------------------------------------------------ player + adapter

export interface YouTubePlayerHandle {
  adapter: RateReportingAdapter;
  /** Resolves on the player's onReady; rejects if the player errors before it is ready. */
  ready: Promise<void>;
  destroy(): void;
  /** The live YT.Player once ready (for module calls the adapter doesn't wrap), else null. */
  raw(): YTPlayer | null;
}

export interface CreateYouTubePlayerOpts {
  /** getCurrentTime() polling period; default 100 ms (~10 Hz). */
  pollMs?: number;
  /** Initial position (playerVars.start, second resolution). */
  startMs?: number;
  /** Player errors after ready (2 invalid id, 5 HTML5, 100 not found, 101/150 not embeddable). */
  onError?: (code: number) => void;
}

// Playing: the poll's own extrapolation drifts by at most one info-delivery interval, and
// getCurrentTime() itself updates in coarse steps — anything past this is a seek.
const SEEK_JUMP_PLAYING_MS = 1500;
// Paused: the position is constant apart from one settle update after the pause lands.
const SEEK_JUMP_PAUSED_MS = 300;
// After our own seekTo(), getCurrentTime() keeps returning the pre-seek position for a poll or
// two (the IFrame API is postMessage-based; nothing is updated optimistically). Polls in that
// window are dropped rather than reported, until the position lands near the target or the
// window expires.
const SEEK_SETTLE_MS = 2000;
const SEEK_LAND_MS = 1500;
// A seek this short cannot be told apart from the stale pre-seek read; accept the first poll.
const SEEK_TINY_MS = 300;

const FALLBACK_RATES = [0.5, 0.75, 1];

interface Sample {
  ms: number;
  wall: number;
  playing: boolean;
}

interface PendingSeek {
  targetMs: number;
  preSeekMs: number;
  issuedWall: number;
  until: number;
  playing: boolean;
}

/**
 * Creates a YouTube player in `el` and returns a PlayerAdapter over it.
 *
 * IMPORTANT: YT.Player REPLACES `el` with the iframe. Pass a throwaway inner <div> that you
 * created for this purpose (e.g. appended to a ref'd container), never a React-managed
 * element — React will otherwise try to reconcile children on a node that no longer exists.
 * On destroy() the iframe is removed; the caller owns the container.
 *
 * The adapter is usable immediately; before `ready` resolves play/pause/setRate are no-ops,
 * seekTo is remembered and applied on ready, availableRates() returns [0.5, 0.75, 1].
 */
export function createYouTubePlayer(
  el: HTMLElement,
  videoId: string,
  opts: CreateYouTubePlayerOpts = {},
): YouTubePlayerHandle {
  const pollMs = opts.pollMs ?? 100;
  const startMs = opts.startMs ?? 0;

  let player: YTPlayer | null = null;
  let isReady = false;
  let destroyed = false;
  let interval: ReturnType<typeof setInterval> | null = null;
  let last: Sample | null = null;
  let pending: PendingSeek | null = null;
  let pendingPreReadySeekMs: number | null = null;

  const timeCbs = new Set<(ms: number) => void>();
  const stateCbs = new Set<(s: 'playing' | 'paused' | 'ended' | 'buffering') => void>();
  const seekCbs = new Set<(ms: number) => void>();
  const rateCbs = new Set<(rate: number) => void>();
  const emit = <T>(set: Set<(v: T) => void>, v: T) => {
    for (const cb of Array.from(set)) cb(v);
  };

  const wall = () => performance.now();
  const rateNow = () => (player && isReady ? player.getPlaybackRate() || 1 : 1);
  const playingNow = () => !!player && isReady && player.getPlayerState() === PS.PLAYING;

  const poll = () => {
    if (!player || !isReady || destroyed) return;
    const now = wall();
    const observed = player.getCurrentTime() * 1000;
    const playing = playingNow();
    const rate = rateNow();

    if (pending) {
      const p = pending;
      const elapsedPlay = p.playing ? (now - p.issuedWall) * rate : 0;
      const nearTarget =
        observed >= p.targetMs - SEEK_LAND_MS &&
        observed <= p.targetMs + elapsedPlay + SEEK_LAND_MS;
      const tiny = Math.abs(p.targetMs - p.preSeekMs) <= SEEK_TINY_MS;
      const landed = nearTarget && (tiny || observed !== p.preSeekMs);
      if (!landed) {
        if (now < p.until) return; // still the stale pre-seek position: drop this poll
        pending = null;
        // Never landed where we asked. Whatever the position is now, the engine's bookkeeping
        // was done for the target — treat it as a seek we did not issue.
        if (!nearTarget) emit(seekCbs, observed);
      } else {
        pending = null;
      }
      last = { ms: observed, wall: now, playing };
      emit(timeCbs, observed);
      return;
    }

    if (last) {
      // Extrapolate from the last sample when either side of the interval was playing (a
      // buffering→playing edge advances less than the wall time — still under the threshold).
      const playingish = playing || last.playing;
      const expected = playingish ? last.ms + (now - last.wall) * rate : last.ms;
      const threshold = playingish ? SEEK_JUMP_PLAYING_MS : SEEK_JUMP_PAUSED_MS;
      // onSeek before onTime: the engine re-derives its boundary tracker from the seek first,
      // then processes the tick at the new position.
      if (Math.abs(observed - expected) > threshold) emit(seekCbs, observed);
    }
    last = { ms: observed, wall: now, playing };
    emit(timeCbs, observed);
  };

  const mapState = (s: number): 'playing' | 'paused' | 'ended' | 'buffering' => {
    switch (s) {
      case PS.PLAYING:
        return 'playing';
      case PS.ENDED:
        return 'ended';
      case PS.BUFFERING:
        return 'buffering';
      default:
        return 'paused'; // PAUSED, CUED, UNSTARTED
    }
  };

  const adapter: RateReportingAdapter = {
    play() {
      if (player && isReady) player.playVideo();
    },
    pause() {
      if (player && isReady) player.pauseVideo();
    },
    seekTo(ms) {
      const target = Math.max(ms, 0);
      if (!player || !isReady) {
        pendingPreReadySeekMs = target;
        return;
      }
      const now = wall();
      const preSeekMs = last?.ms ?? player.getCurrentTime() * 1000;
      const playing = playingNow();
      player.seekTo(target / 1000, true);
      pending = {
        targetMs: target,
        preSeekMs,
        issuedWall: now,
        until: now + SEEK_SETTLE_MS,
        playing,
      };
      // Baseline on the target so the landing is not read as a discontinuity.
      last = { ms: target, wall: now, playing };
      // Report our own seek synchronously, like <video>'s `seeking` event does for programmatic
      // seeks: the engine set its internalSeek flag before calling us and consumes it here.
      // Without this a small seek (below the discontinuity threshold) would leave the flag set
      // and swallow the next real native seek.
      emit(seekCbs, target);
    },
    currentTimeMs() {
      if (pending) return pending.targetMs; // getCurrentTime() lags our seek
      if (player && isReady) return player.getCurrentTime() * 1000;
      return pendingPreReadySeekMs ?? startMs;
    },
    durationMs() {
      if (!player || !isReady) return null;
      const d = player.getDuration();
      return d > 0 ? d * 1000 : null;
    },
    isPlaying() {
      return playingNow();
    },
    setRate(rate) {
      if (player && isReady) player.setPlaybackRate(rate);
    },
    availableRates() {
      if (!player || !isReady) return FALLBACK_RATES.slice();
      const rates = player
        .getAvailablePlaybackRates()
        .filter((r) => r >= 0.5 && r <= 1)
        .sort((a, b) => a - b);
      return rates.length ? rates : FALLBACK_RATES.slice();
    },
    onTime(cb) {
      timeCbs.add(cb);
      return () => {
        timeCbs.delete(cb);
      };
    },
    onStateChange(cb) {
      stateCbs.add(cb);
      return () => {
        stateCbs.delete(cb);
      };
    },
    onSeek(cb) {
      seekCbs.add(cb);
      return () => {
        seekCbs.delete(cb);
      };
    },
    onRateChange(cb) {
      rateCbs.add(cb);
      return () => {
        rateCbs.delete(cb);
      };
    },
  };

  const ready = new Promise<void>((resolve, reject) => {
    loadYouTubeIframeApi().then(
      (yt) => {
        if (destroyed) return; // destroyed before the API arrived: `ready` stays pending
        const playerVars: Record<string, string | number> = {
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          enablejsapi: 1,
          origin: window.location.origin,
        };
        if (startMs > 0) playerVars.start = Math.floor(startMs / 1000);
        player = new yt.Player(el, {
          videoId,
          width: '100%',
          height: '100%',
          playerVars,
          events: {
            onReady: () => {
              if (destroyed) return;
              isReady = true;
              if (pendingPreReadySeekMs != null) {
                const ms = pendingPreReadySeekMs;
                pendingPreReadySeekMs = null;
                adapter.seekTo(ms);
              }
              interval = setInterval(poll, pollMs);
              resolve();
            },
            onStateChange: (e) => {
              if (destroyed) return;
              emit(stateCbs, mapState(e.data));
            },
            onPlaybackRateChange: (e) => {
              if (destroyed) return;
              emit(rateCbs, e.data);
            },
            onError: (e) => {
              if (destroyed) return;
              if (!isReady) reject(new Error(`YouTube player error ${e.data}`));
              opts.onError?.(e.data);
            },
          },
        });
      },
      (err) => {
        if (!destroyed) reject(err);
      },
    );
  });
  // Callers that only await `ready` in an effect may never attach a catch; keep the rejection
  // from surfacing as unhandled while still rejecting for those who do await it.
  ready.catch(() => {});

  return {
    adapter,
    ready,
    raw() {
      return player && isReady && !destroyed ? player : null;
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      if (interval != null) clearInterval(interval);
      interval = null;
      timeCbs.clear();
      stateCbs.clear();
      seekCbs.clear();
      rateCbs.clear();
      try {
        player?.destroy();
      } catch {
        /* iframe already gone */
      }
      player = null;
    },
  };
}
